/**
 * ============================================================
 * LOVEINDIA.RO - CUSTOM THEME JAVASCRIPT
 * ============================================================
 *
 * IMPORTANT AFTER ETHEREAL / ETHERYX THEME UPDATE
 *
 * This file contains custom functionality that is NOT part
 * of the original Ethereal theme.
 *
 * After every theme update check:
 *
 * 1. assets/custom.js exists in the UPDATED theme.
 *
 * 2. layout/theme.liquid contains, before </body>:
 *
 *    <script src="{{ 'custom.js' | asset_url }}" defer></script>
 *
 * 3. Test:
 *    - Spiritual free book picker
 *    - Cookie banner + Newsletter popup
 *
 * If Shopify does not migrate the custom code automatically,
 * copy this file manually to the new theme and add the
 * theme.liquid include again.
 *
 * Last reviewed: 2026-09-11
 * ============================================================
 */


/**
 * ============================================================
 * 02. NEWSLETTER / SHOPIFY COOKIE CONSENT
 * ============================================================
 *
 * Ethereal normally starts the Newsletter trigger immediately
 * after page load.
 *
 * This patch prevents the Newsletter from opening while the
 * Shopify cookie consent banner is waiting for a decision.
 *
 * For the "delay" trigger, the configured Ethereal delay starts
 * after the visitor handles the cookie banner.
 */

(() => {

  const NEWSLETTER_TAG = 'coretex-newsletter';

  let consentChangedAt = null;


  /*
   * Remember when the visitor handled the Shopify cookie banner.
   */
  document.addEventListener(
    'visitorConsentCollected',
    () => {
      consentChangedAt = Date.now();
    }
  );


  /*
   * Make sure Shopify Customer Privacy API is available.
   */
  const loadPrivacyAPI = () => {

    return new Promise(resolve => {

      if (window.Shopify?.customerPrivacy) {
        resolve(true);
        return;
      }

      if (!window.Shopify?.loadFeatures) {
        resolve(false);
        return;
      }

      window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1'
          }
        ],
        error => {

          resolve(
            !error &&
            !!window.Shopify?.customerPrivacy
          );

        }
      );

    });

  };


  /*
   * Wait until Ethereal registers its Newsletter component.
   */
  customElements
    .whenDefined(NEWSLETTER_TAG)
    .then(() => {

      const Newsletter =
        customElements.get(NEWSLETTER_TAG);

      if (!Newsletter) return;


      /*
       * Prevent applying the patch twice.
       */
      if (
        Newsletter.prototype.__loveIndiaCookieConsentPatch
      ) {
        return;
      }


      const originalTriggerOpen =
        Newsletter.prototype._triggerOpen;


      Newsletter.prototype._triggerOpen = function () {

        const newsletter = this;


        /*
         * Prevent multiple consent listeners.
         */
        if (
          newsletter.__waitingForCookieDecision
        ) {
          return;
        }


        const openNewsletter = () => {
          originalTriggerOpen.call(newsletter);
        };


        loadPrivacyAPI().then(privacyLoaded => {

          /*
           * Fail gracefully.
           * Keep original Ethereal behaviour if the
           * Shopify Privacy API isn't available.
           */
          if (!privacyLoaded) {
            openNewsletter();
            return;
          }


          const privacy =
            window.Shopify.customerPrivacy;


          if (
            typeof privacy.shouldShowBanner !== 'function'
          ) {
            openNewsletter();
            return;
          }


          /*
           * Cookie banner has already been handled.
           */
          if (!privacy.shouldShowBanner()) {

            /*
             * If consent was handled during this page view,
             * preserve the Newsletter delay AFTER consent.
             */
            if (
              consentChangedAt &&
              newsletter.dataset.trigger === 'delay'
            ) {

              const delay =
                (
                  parseInt(
                    newsletter.dataset.delay,
                    10
                  ) || 0
                ) * 1000;


              const elapsed =
                Date.now() - consentChangedAt;


              const remaining =
                Math.max(
                  0,
                  delay - elapsed
                );


              if (remaining > 0) {

                setTimeout(
                  openNewsletter,
                  remaining
                );

              } else {

                openNewsletter();

              }


              return;
            }


            /*
             * Returning visitor.
             * Cookie decision already existed when page loaded.
             */
            openNewsletter();

            return;
          }


          /*
           * Cookie banner is still waiting for a decision.
           */
          newsletter.__waitingForCookieDecision = true;


          document.addEventListener(
            'visitorConsentCollected',
            () => {

              newsletter.__waitingForCookieDecision = false;


              /*
               * For Time Delay trigger, start the Ethereal
               * delay after cookie consent.
               */
              const delay =
                newsletter.dataset.trigger === 'delay'
                  ? (
                      parseInt(
                        newsletter.dataset.delay,
                        10
                      ) || 0
                    ) * 1000
                  : 0;


              setTimeout(
                openNewsletter,
                delay
              );

            },
            {
              once: true
            }
          );

        });

      };


      Newsletter.prototype.__loveIndiaCookieConsentPatch = true;

    });

})();